/**
 * Translation Service - Frontend DeepL Integration
 * 
 * Handles translation of UI strings and server responses using DeepL API
 * with two-tier caching strategy:
 * - localStorage for static UI translations (persistent)
 * - sessionStorage for dynamic server responses (temporary)
 */

// Simple hash function for cache keys
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// Rate limiting queue
class TranslationQueue {
  constructor(maxRequestsPerMinute = 20) {
    this.queue = [];
    this.processing = false;
    this.maxRequestsPerMinute = maxRequestsPerMinute;
    this.requestTimestamps = [];
  }

  async add(translationFn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ translationFn, resolve, reject });
      this.process();
    });
  }

  async process() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    while (this.queue.length > 0) {
      // Remove timestamps older than 1 minute
      const oneMinuteAgo = Date.now() - 60000;
      this.requestTimestamps = this.requestTimestamps.filter(ts => ts > oneMinuteAgo);

      // Check if we can make a request
      if (this.requestTimestamps.length >= this.maxRequestsPerMinute) {
        // Wait until we can make another request
        const oldestTimestamp = this.requestTimestamps[0];
        const waitTime = oldestTimestamp + 60000 - Date.now();
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }

      const item = this.queue.shift();
      this.requestTimestamps.push(Date.now());

      try {
        const result = await item.translationFn();
        item.resolve(result);
      } catch (error) {
        item.reject(error);
      }
    }

    this.processing = false;
  }
}

class TranslationService {
  constructor() {
    this.apiKey = null;
    this.apiUrl = 'https://api-free.deepl.com/v2/translate';
    this.sourceLanguage = 'FR';
    this.defaultLanguage = 'fr';
    this.queue = new TranslationQueue(20); // 20 requests per minute
    this.dictionaryVersion = '1.3'; // Increment when dictionary changes

    // Supported languages map
    this.supportedLanguages = {
      'en': 'EN',
      'fr': 'FR'
    };
  }

  /**
   * Initialize the service with API key and configuration
   */
  init(config = {}) {
    // IMPORTANT: Always go through gateway (port 9200), not directly to translation service (port 7002)
    this.apiUrl = config.apiUrl || process.env.REACT_APP_TRANSLATION_API_URL || 'https://corearena.tv/api/translate';
    this.defaultLanguage = config.defaultLanguage || 'fr';

    console.log('✅ [Translation] Service initialized:', {
      apiUrl: this.apiUrl,
      defaultLanguage: this.defaultLanguage,
      currentLanguage: this.getCurrentLanguage()
    });
  }

  /**
   * Check if a language is supported
   */
  isLanguageSupported(langCode) {
    return langCode in this.supportedLanguages;
  }

  /**
   * Get DeepL language code from our language code
   */
  getDeepLLanguageCode(langCode) {
    return this.supportedLanguages[langCode] || 'EN';
  }

  /**
   * Call backend translation API (proxy to DeepL)
   */
  async callDeepLAPI(text, targetLang) {
    if (!text || text.trim() === '') {
      return text;
    }

    const deepLTargetLang = this.getDeepLLanguageCode(targetLang);

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          source_lang: this.sourceLanguage,
          target_lang: deepLTargetLang,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Translation API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.translation) {
        return data.translation;
      }

      throw new Error('No translation returned from API');
    } catch (error) {
      console.error('❌ [Translation] API error:', error);
      throw error;
    }
  }

  /**
   * Translate text with rate limiting
   */
  async translateWithQueue(text, targetLang) {
    return this.queue.add(() => this.callDeepLAPI(text, targetLang));
  }

  /**
   * Get UI translations cache key
   */
  getUICacheKey(langCode) {
    return `ui_translations_${langCode}_v${this.dictionaryVersion}`;
  }

  /**
   * Get response cache key
   */
  getResponseCacheKey(langCode) {
    return `response_cache_${langCode}`;
  }

  /**
   * Load cached UI translations for a language
   */
  loadUICache(langCode) {
    try {
      const cacheKey = this.getUICacheKey(langCode);
      const cached = localStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error('❌ [Translation] Error loading UI cache:', error);
      return {};
    }
  }

  /**
   * Save UI translations cache
   */
  saveUICache(langCode, translations) {
    try {
      const cacheKey = this.getUICacheKey(langCode);
      localStorage.setItem(cacheKey, JSON.stringify(translations));
    } catch (error) {
      console.error('❌ [Translation] Error saving UI cache:', error);
    }
  }

  /**
   * Load cached response translations
   */
  loadResponseCache(langCode) {
    try {
      const cacheKey = this.getResponseCacheKey(langCode);
      const cached = sessionStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error('❌ [Translation] Error loading response cache:', error);
      return {};
    }
  }

  /**
   * Save response translation cache
   */
  saveResponseCache(langCode, translations) {
    try {
      const cacheKey = this.getResponseCacheKey(langCode);
      sessionStorage.setItem(cacheKey, JSON.stringify(translations));
    } catch (error) {
      console.error('❌ [Translation] Error saving response cache:', error);
    }
  }

  /**
   * Translate a UI string (uses localStorage cache)
   */
  async translateUI(key, sourceText, targetLang) {
    // If target language is French (source), return original
    if (targetLang === 'fr') {
      return sourceText;
    }

    if (!this.isLanguageSupported(targetLang)) {
      console.warn(`⚠️ [Translation] Unsupported language: ${targetLang}`);
      return sourceText;
    }

    // Check cache first
    const cache = this.loadUICache(targetLang);
    if (cache[key]) {
      return cache[key];
    }

    // Translate and cache
    try {
      const translated = await this.translateWithQueue(sourceText, targetLang);
      cache[key] = translated;
      this.saveUICache(targetLang, cache);
      return translated;
    } catch (error) {
      console.error(`❌ [Translation] Failed to translate UI key "${key}":`, error);
      return sourceText; // Fallback to source
    }
  }

  /**
   * Translate dynamic text (uses sessionStorage cache)
   */
  async translateText(text, targetLang) {
    // If target language is French (source), return original
    if (targetLang === 'fr') {
      return text;
    }

    if (!text || text.trim() === '') {
      return text;
    }

    if (!this.isLanguageSupported(targetLang)) {
      console.warn(`⚠️ [Translation] Unsupported language: ${targetLang}`);
      return text;
    }

    // Check cache first
    const textHash = simpleHash(text);
    const cache = this.loadResponseCache(targetLang);

    if (cache[textHash]) {
      return cache[textHash];
    }

    // Translate and cache
    try {
      const translated = await this.translateWithQueue(text, targetLang);
      cache[textHash] = translated;
      this.saveResponseCache(targetLang, cache);
      return translated;
    } catch (error) {
      console.error('❌ [Translation] Failed to translate text:', error);
      return text; // Fallback to original
    }
  }

  /**
   * Batch translate multiple UI strings
   */
  async batchTranslateUI(items, targetLang) {
    if (targetLang === 'fr') {
      return items.reduce((acc, item) => {
        acc[item.key] = item.text;
        return acc;
      }, {});
    }

    const cache = this.loadUICache(targetLang);
    const results = {};
    const toTranslate = [];

    // Check cache first
    items.forEach(item => {
      if (cache[item.key]) {
        results[item.key] = cache[item.key];
      } else {
        toTranslate.push(item);
      }
    });

    // Translate remaining items
    for (const item of toTranslate) {
      try {
        const translated = await this.translateUI(item.key, item.text, targetLang);
        results[item.key] = translated;
      } catch (error) {
        console.error(`❌ [Translation] Failed to translate "${item.key}":`, error);
        results[item.key] = item.text;
      }
    }

    return results;
  }

  /**
   * Clear all caches
   */
  clearCache() {
    try {
      // Clear UI translations for all languages
      Object.keys(this.supportedLanguages).forEach(langCode => {
        localStorage.removeItem(this.getUICacheKey(langCode));
        sessionStorage.removeItem(this.getResponseCacheKey(langCode));
      });
      console.log('✅ [Translation] Cache cleared');
    } catch (error) {
      console.error('❌ [Translation] Error clearing cache:', error);
    }
  }

  /**
   * Clear cache for specific language
   */
  clearLanguageCache(langCode) {
    try {
      localStorage.removeItem(this.getUICacheKey(langCode));
      sessionStorage.removeItem(this.getResponseCacheKey(langCode));
      console.log(`✅ [Translation] Cache cleared for ${langCode}`);
    } catch (error) {
      console.error('❌ [Translation] Error clearing language cache:', error);
    }
  }

  /**
   * Get current language preference
   */
  getCurrentLanguage() {
    return localStorage.getItem('preferred_language') || this.defaultLanguage;
  }

  /**
   * Set current language preference
   */
  setCurrentLanguage(langCode) {
    if (!this.isLanguageSupported(langCode)) {
      console.warn(`⚠️ [Translation] Unsupported language: ${langCode}`);
      return;
    }
    localStorage.setItem('preferred_language', langCode);
  }
}

// Export singleton instance
const translationService = new TranslationService();
export default translationService;

